import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { bool, func, instanceOf, object, shape, string } from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import {
  AvatarMedium,
  ExtendBookingBreakDown,
  IconSpinner,
  Logo,
  NamedLink,
  NamedRedirect,
  Page,
  PrimaryButton,
  ResponsiveImage,
  SecondaryButton,
} from '../../components';
import config from '../../config';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { savePaymentMethod } from '../../ducks/paymentMethods.duck';
import { confirmCardPayment, handleCardSetup } from '../../ducks/stripe.duck';
import { StripePaymentForm } from '../../forms';
import routeConfiguration from '../../routeConfiguration';
import { formatMoney } from '../../util/currency';
import {
  TRANSITION_DECLINE_EXTEND_BOOKING,
  TRANSITION_SAVE_EXTEND_BOOKING,
  txIsOfferExtendBooking,
} from '../../util/customTransaction/mainRentalTransaction';
import {
  ensureBooking,
  ensureCurrentUser,
  ensureListing,
  ensurePaymentMethodCard,
  ensureStripeCustomer,
  ensureTransaction,
  ensureUser,
} from '../../util/data';
import { dateFromLocalToAPI, getDateForEmail, minutesBetween } from '../../util/dates';
import {
  isTransactionChargeDisabledError,
  isTransactionInitiateAmountTooLowError,
  isTransactionInitiateBookingTimeNotAvailableError,
  isTransactionInitiateListingNotFoundError,
  isTransactionInitiateMissingStripeAccountError,
  isTransactionZeroPaymentError,
  transactionInitiateOrderStripeErrors,
} from '../../util/errors';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { findRouteByRouteName, pathByRouteName } from '../../util/routes';
import {
  DATE_TYPE_DATE,
  EXTEND_RENTAL,
  INSTANT_RENTAL,
  MAIN_RENTAL,
  propTypes,
} from '../../util/types';
import { createSlug } from '../../util/urlHelpers';
import { savePhoneNumber } from '../ContactDetailsPage/ContactDetailsPage.duck';
import { createStripeSetupIntent } from '../PaymentMethodsPage/PaymentMethodsPage.duck';
import { clearData, extendStoreData, extendStoredData } from './CheckoutPageSessionHelpers';
import {
  confirmPayment,
  initiateOrder,
  sendMessage,
  setInitialValues,
  speculateTransaction,
  stripeCustomer,
  transit,
} from './ExtendCheckoutPage.duck';

import { retrievePaymentIntent } from '../../ducks/stripe.duck';
import {
  TRANSITION_EXTEND_DECLINE_EXTEND_BOOKING,
  TRANSITION_EXTEND_SAVE_EXTEND_BOOKING,
  exTxIsExpirePayment,
  exTxIsOfferExtendBooking,
  exTxIsRequestPayment,
} from '../../util/customTransaction/extendRentalTransaction';
import css from './CheckoutPage.css';

const MAIN_RENTAL_PROCESS = [MAIN_RENTAL, INSTANT_RENTAL];

const STORAGE_KEY = 'ExtendCheckoutPage';

// Payment charge options
const ONETIME_PAYMENT = 'ONETIME_PAYMENT';
const PAY_AND_SAVE_FOR_LATER_USE = 'PAY_AND_SAVE_FOR_LATER_USE';
const USE_SAVED_CARD = 'USE_SAVED_CARD';

const STRIPE_PI_USER_ACTIONS_DONE_STATUSES = ['processing', 'requires_capture', 'succeeded'];

const paymentFlow = (selectedPaymentMethod, saveAfterOnetimePayment) => {
  // Payment mode could be 'replaceCard', but without explicit saveAfterOnetimePayment flag,
  // we'll handle it as one-time payment
  return selectedPaymentMethod === 'defaultCard'
    ? USE_SAVED_CARD
    : saveAfterOnetimePayment
    ? PAY_AND_SAVE_FOR_LATER_USE
    : ONETIME_PAYMENT;
};

const initializeOrderPage = (initialValues, routes, dispatch) => {
  const OrderPage = findRouteByRouteName('OrderDetailsPage', routes);

  // Transaction is already created, but if the initial message
  // sending failed, we tell it to the OrderDetailsPage.
  dispatch(OrderPage.setInitialValues(initialValues));
};

const checkIsPaymentExpired = existingTransaction => {
  return exTxIsExpirePayment(existingTransaction)
    ? true
    : exTxIsRequestPayment(existingTransaction)
    ? minutesBetween(existingTransaction.attributes.lastTransitionedAt, new Date()) >= 15
    : false;
};

const getClientSecret = setupIntent => {
  return setupIntent && setupIntent.attributes ? setupIntent.attributes.clientSecret : null;
};

export class ExtendCheckoutPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      pageData: {},
      dataLoaded: false,
      submitting: false,
    };

    this.stripe = null;
    this.onStripeInitialized = this.onStripeInitialized.bind(this);
    this.loadInitialData = this.loadInitialData.bind(this);
    this.handlePaymentIntent = this.handlePaymentIntent.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    if (window) {
      this.loadInitialData();
    }
  }

  onStripeInitialized(stripe) {
    this.stripe = stripe;

    const { paymentIntent, onRetrievePaymentIntent } = this.props;
    const tx = this.state.pageData ? this.state.pageData.transaction : null;

    // We need to get up to date PI, if booking is created but payment is not expired.
    const shouldFetchPaymentIntent =
      this.stripe &&
      !paymentIntent &&
      tx &&
      tx.id &&
      tx.booking &&
      tx.booking.id &&
      exTxIsRequestPayment(tx);

    if (shouldFetchPaymentIntent) {
      const { stripePaymentIntentClientSecret } =
        tx.attributes.protectedData && tx.attributes.protectedData.stripePaymentIntents
          ? tx.attributes.protectedData.stripePaymentIntents.default
          : {};

      // Fetch up to date PaymentIntent from Stripe
      onRetrievePaymentIntent({ stripe, stripePaymentIntentClientSecret });
    }
  }

  loadInitialData() {
    const {
      bookingData,
      listing,
      transaction,
      fetchSpeculatedTransaction,
      fetchStripeCustomer,
      history,
    } = this.props;

    fetchStripeCustomer();

    const hasNavigatedThroughLink = history.action === 'PUSH' || history.action === 'REPLACE';

    const hasDataInProps = !!(bookingData && listing) && hasNavigatedThroughLink;

    if (hasDataInProps) {
      extendStoreData({ bookingData, listing, transaction, storageKey: STORAGE_KEY });
    }

    // NOTE: stored data can be empty if user has already successfully completed transaction.
    const pageData = hasDataInProps
      ? { bookingData, listing, transaction }
      : extendStoredData(STORAGE_KEY);

    // Check if a booking is already created according to stored data.
    const tx = pageData ? pageData.transaction : null;
    const isBookingCreated = tx && tx.booking && tx.booking.id;

    const currentTransaction =
      pageData && pageData.bookingData && pageData.bookingData.currentTransaction;
    const { extendBookingData } =
      currentTransaction && currentTransaction.attributes
        ? currentTransaction.attributes.protectedData
        : {};

    const currentTransactionId =
      currentTransaction && currentTransaction.id && currentTransaction.id.uuid;

    const shouldFetchSpeculatedTransaction =
      pageData &&
      pageData.listing &&
      pageData.listing.id &&
      pageData.bookingData &&
      extendBookingData &&
      extendBookingData.startDate &&
      extendBookingData.endDate &&
      extendBookingData.extendPrice &&
      !isBookingCreated;

    if (shouldFetchSpeculatedTransaction) {
      const listingId = pageData.listing.id;

      const { startDate, endDate, extendPrice, seats } = extendBookingData;

      const NOW = new Date();
      let bookingStartForAPI = dateFromLocalToAPI(startDate);

      if (moment(startDate).isBefore(NOW)) {
        bookingStartForAPI = dateFromLocalToAPI(NOW);
      }

      const bookingEndForAPI = dateFromLocalToAPI(endDate);

      fetchSpeculatedTransaction(
        {
          listingId,
          bookingStart: bookingStartForAPI,
          bookingEnd: bookingEndForAPI,
          bookingDisplayStart: moment(startDate).toDate(),
          bookingDisplayEnd: moment(endDate).toDate(),
          extendPrice,
          seats,
        },
        currentTransactionId
      );
    }

    this.setState({ pageData: pageData || {}, dataLoaded: true });
  }

  handlePaymentIntent(handlePaymentParams) {
    const {
      currentUser,
      stripeCustomerFetched,
      onConfirmCardPayment,
      onConfirmPayment,
      onSendMessage,
      onSavePaymentMethod,
      onCreateSetupIntent,
      onHandleCardSetup,
      onInitiateOrder,
      speculatedTransaction,
      onTransit,
    } = this.props;
    const {
      pageData,
      message,
      selectedPaymentMethod,
      saveAfterOnetimePayment,
      phoneNumber,
    } = handlePaymentParams;
    const storedTx = ensureTransaction(pageData.transaction);
    const { bookingData } = pageData;
    const { mainTransactionId, currentTransaction } = bookingData;
    const { extendBookingData } =
      currentTransaction && currentTransaction.attributes
        ? currentTransaction.attributes.protectedData
        : {};

    const { extendPrice, seats } = extendBookingData || {};
    const freeFee = extendPrice.amount === 0;

    const ensuredCurrentUser = ensureCurrentUser(currentUser);
    const ensuredStripeCustomer = ensureStripeCustomer(ensuredCurrentUser.stripeCustomer);
    const ensuredDefaultPaymentMethod = ensurePaymentMethodCard(
      ensuredStripeCustomer.defaultPaymentMethod
    );

    const hasDefaultPaymentMethod = !!(
      stripeCustomerFetched &&
      ensuredStripeCustomer.attributes.stripeCustomerId &&
      ensuredDefaultPaymentMethod.id
    );

    const stripePaymentMethodId = hasDefaultPaymentMethod
      ? ensuredDefaultPaymentMethod.attributes.stripePaymentMethodId
      : null;

    const selectedPaymentFlow = paymentFlow(selectedPaymentMethod, saveAfterOnetimePayment);

    const fnInitiateOrder = fnParams => {
      const hasPaymentIntents =
        storedTx.attributes.protectedData && storedTx.attributes.protectedData.stripePaymentIntents;

      return hasPaymentIntents ? Promise.resolve(storedTx) : onInitiateOrder(fnParams, storedTx.id);
    };

    const fnSaveExtendBooking = fnParams => {
      const params = {
        protectedData: {
          extendTransactionId: fnParams.id.uuid,
        },
      };

      const order = ensureTransaction(fnParams);

      if (order.id) {
        const { bookingData, listing } = pageData;
        extendStoreData({ bookingData, listing, order, storageKey: STORAGE_KEY });
        this.setState({ pageData: { ...pageData, transaction: order } });
      }

      if (currentTransaction) {
        const { processName } = currentTransaction.attributes;

        const transition =
          processName === EXTEND_RENTAL
            ? TRANSITION_EXTEND_SAVE_EXTEND_BOOKING
            : TRANSITION_SAVE_EXTEND_BOOKING;

        return onTransit(currentTransaction.id, transition, params).then(() => fnParams);
      } else {
        return Promise.resolve(fnParams);
      }
    };

    // Step 2: pay using Stripe SDK
    const fnConfirmCardPayment = fnParams => {
      const order = ensureTransaction(fnParams);

      const hasPaymentIntents =
        order.attributes.protectedData && order.attributes.protectedData.stripePaymentIntents;

      const { stripePaymentIntentClientSecret } = hasPaymentIntents
        ? order.attributes.protectedData.stripePaymentIntents.default
        : null;

      const { stripe, card, billingDetails, paymentIntent } = handlePaymentParams;
      const stripeElementMaybe = selectedPaymentFlow !== USE_SAVED_CARD ? { card } : {};

      const paymentParams =
        selectedPaymentFlow !== USE_SAVED_CARD
          ? {
              payment_method: {
                billing_details: billingDetails,
                card: card,
              },
            }
          : {};

      const params = {
        stripePaymentIntentClientSecret,
        orderId: order.id,
        stripe,
        ...stripeElementMaybe,
        paymentParams,
      };

      // If paymentIntent status is not waiting user action,
      // confirmCardPayment has been called previously.
      const hasPaymentIntentUserActionsDone =
        paymentIntent && STRIPE_PI_USER_ACTIONS_DONE_STATUSES.includes(paymentIntent.status);

      return hasPaymentIntentUserActionsDone
        ? Promise.resolve({ transactionId: order.id, paymentIntent })
        : onConfirmCardPayment(params);
    };

    // Step 3: complete order by confirming payment to Marketplace API
    // Parameter should contain { paymentIntent, transactionId } returned in step 2
    const fnConfirmPayment = fnParams => {
      return onConfirmPayment(fnParams);
    };

    // Step 4: send initial message
    const fnSendMessage = fnParams => {
      return onSendMessage({ ...fnParams, message });
    };

    // Step 5: optionally save card as defaultPaymentMethod
    const fnSavePaymentMethod = fnParams => {
      if (selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE) {
        const { card, billingDetails } = handlePaymentParams;

        return onCreateSetupIntent()
          .then(setupIntent => {
            const stripeParams = {
              stripe: this.stripe,
              card,
              setupIntentClientSecret: getClientSecret(setupIntent),
              paymentParams: {
                payment_method_data: {
                  billing_details: billingDetails,
                },
              },
            };

            return onHandleCardSetup(stripeParams);
          })
          .then(result => {
            const newPaymentMethod = result.setupIntent.payment_method;
            return onSavePaymentMethod(ensuredStripeCustomer, newPaymentMethod);
          })
          .then(response => {
            if (response.errors) {
              return { ...fnParams, paymentMethodSaved: false };
            }
            return { ...fnParams, paymentMethodSaved: true };
          })
          .catch(e => {
            // Real error cases are catched already in paymentMethods page.
            return { ...fnParams, paymentMethodSaved: false };
          });
      } else {
        return Promise.resolve({ ...fnParams, paymentMethodSaved: true });
      }
    };

    const applyAsync = (acc, val) => acc.then(val);
    const composeAsync = (...funcs) => x => funcs.reduce(applyAsync, Promise.resolve(x));
    const handlePaymentIntentCreation = composeAsync(
      fnInitiateOrder,
      fnSaveExtendBooking,
      fnConfirmCardPayment,
      fnConfirmPayment,
      fnSendMessage,
      fnSavePaymentMethod
    );

    const handlePaymentOfFreeFee = composeAsync(fnInitiateOrder, fnSavePaymentMethod);

    const tx = speculatedTransaction ? speculatedTransaction : storedTx;

    const optionalPaymentParams =
      selectedPaymentFlow === USE_SAVED_CARD && hasDefaultPaymentMethod
        ? { paymentMethod: stripePaymentMethodId }
        : selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE
        ? { setupPaymentMethodForSaving: true }
        : {};

    const bondReleaseTime = moment(tx.booking.attributes.displayEnd).add(60, 'hours');

    const orderParams = {
      listingId: pageData.listing.id,
      bookingStart: tx.booking.attributes.start,
      bookingEnd: tx.booking.attributes.end,
      bookingDisplayStart: tx.booking.attributes.displayStart,
      bookingDisplayEnd: moment(tx.booking.attributes.displayEnd)
        .subtract(1, 'day')
        .endOf('day')
        .toDate(),
      extendPrice,
      freeFee,
      protectedData: {
        currentYear: new Date().getFullYear().toString(),
        phoneNumber: phoneNumber,
        mainTransactionId: mainTransactionId.uuid,
        prevTransactionId:
          currentTransaction && currentTransaction.id ? currentTransaction.id.uuid : null,
        bondReleaseTime: getDateForEmail(bondReleaseTime),
        seats,
      },
      seats,
      ...optionalPaymentParams,
    };

    return freeFee ? handlePaymentOfFreeFee(orderParams) : handlePaymentIntentCreation(orderParams);
  }

  handleSubmit(values) {
    if (this.state.submitting) {
      return;
    }
    this.setState({ submitting: true });

    const {
      history,
      speculatedTransaction,
      currentUser,
      paymentIntent,
      dispatch,
      onSavePhoneNumber,
    } = this.props;

    const { card, message, paymentMethod, formValues } = values;

    const {
      name,
      addressLine1,
      addressLine2,
      postal,
      city,
      state,
      country,
      saveAfterOnetimePayment,
      phoneNumber,
    } = formValues || {};

    const addressMaybe =
      addressLine1 && postal
        ? {
            address: {
              city: city,
              country: country,
              line1: addressLine1,
              line2: addressLine2,
              postal_code: postal,
              state: state,
            },
          }
        : {};
    const billingDetails = {
      name,
      email: ensureCurrentUser(currentUser).attributes.email,
      ...addressMaybe,
    };

    const requestPaymentParams = {
      pageData: this.state.pageData,
      speculatedTransaction,
      stripe: this.stripe,
      card,
      billingDetails,
      message,
      paymentIntent,
      selectedPaymentMethod: paymentMethod,
      saveAfterOnetimePayment: !!saveAfterOnetimePayment,
      phoneNumber,
    };

    const { bookingData } = this.state.pageData;
    const mainTransactionId = bookingData.mainTransactionId;

    this.handlePaymentIntent(requestPaymentParams)
      .then(res => {
        const { orderId, messageSuccess, paymentMethodSaved } = res;
        this.setState({ submitting: false });

        const ensuredCurrentUser = ensureCurrentUser(currentUser);
        const protectedData = ensuredCurrentUser.attributes.profile;
        const { phoneNumber: currentPhoneNumber } = protectedData || {};
        if (phoneNumber && !isEqual(phoneNumber, currentPhoneNumber)) {
          onSavePhoneNumber({ phoneNumber });
        }

        const routes = routeConfiguration();

        let orderDetailsPath = pathByRouteName('InboxPage', routes, {
          tab: 'orders',
        });

        if (mainTransactionId) {
          orderDetailsPath = pathByRouteName('OrderDetailsPage', routes, {
            id: mainTransactionId.uuid,
          });
        }

        const initialMessageFailedToTransaction = messageSuccess ? null : orderId;

        const initialValues = {
          initialMessageFailedToTransaction,
          savePaymentMethodFailed: !paymentMethodSaved,
        };

        initializeOrderPage(initialValues, routes, dispatch);
        clearData(STORAGE_KEY);
        history.push(orderDetailsPath);
      })
      .catch(err => {
        console.error(err);
        this.setState({ submitting: false });
      });
  }

  handleDecline = currentTransaction => {
    if (this.state.submitting) {
      return;
    }

    const { history, onTransit } = this.props;

    const transactionId = currentTransaction.id;
    const processName = currentTransaction.attributes.processName;

    const { bookingData } = this.state.pageData;
    const { mainTransactionId } = bookingData || {};

    this.setState({ submitting: true });

    const transition = MAIN_RENTAL_PROCESS.includes(processName)
      ? TRANSITION_DECLINE_EXTEND_BOOKING
      : TRANSITION_EXTEND_DECLINE_EXTEND_BOOKING;

    onTransit(transactionId, transition)
      .then(() => {
        this.setState({ submitting: false });
        const routes = routeConfiguration();

        let orderDetailsPath = pathByRouteName('InboxPage', routes, {
          tab: 'orders',
        });

        if (mainTransactionId) {
          orderDetailsPath = pathByRouteName('OrderDetailsPage', routes, {
            id: mainTransactionId.uuid,
          });
        }

        clearData(STORAGE_KEY);
        history.push(orderDetailsPath);
      })
      .catch(err => {
        console.error(err);
        this.setState({ submitting: false });
      });
  };

  render() {
    const {
      scrollingDisabled,
      speculateTransactionInProgress,
      speculateTransactionError,
      speculatedTransaction: speculatedTransactionMaybe,
      initiateOrderError,
      confirmPaymentError,
      intl,
      params,
      currentUser,
      confirmCardPaymentError,
      paymentIntent,
      retrievePaymentIntentError,
      stripeCustomerFetched,
      transitError,
    } = this.props;

    const listingNotFound =
      isTransactionInitiateListingNotFoundError(speculateTransactionError) ||
      isTransactionInitiateListingNotFoundError(initiateOrderError);

    const isLoading = !this.state.dataLoaded || speculateTransactionInProgress;

    const { listing, transaction, bookingData } = this.state.pageData;

    const existingTransaction = ensureTransaction(transaction);
    const speculatedTransaction = ensureTransaction(speculatedTransactionMaybe, {}, null);
    const currentListing = ensureListing(listing);
    const currentAuthor = ensureUser(currentListing.author);

    const listingTitle = currentListing.attributes.title;
    const title = intl.formatMessage({ id: 'ExtendCheckoutPage.title' }, { listingTitle });

    const pageProps = { title, scrollingDisabled };
    const topbar = (
      <div className={css.topbar}>
        <NamedLink className={css.home} name="LandingPage">
          <Logo
            className={css.logoMobile}
            title={intl.formatMessage({ id: 'CheckoutPage.goToLandingPage' })}
            format="mobile"
          />
          <Logo
            className={css.logoDesktop}
            alt={intl.formatMessage({ id: 'CheckoutPage.goToLandingPage' })}
            format="desktop"
          />
        </NamedLink>
      </div>
    );

    if (isLoading) {
      return (
        <Page {...pageProps}>
          {topbar}
          <div className={css.loadingContainer}>
            <IconSpinner />
            <FormattedMessage id="CheckoutPage.loadingData" />
          </div>
        </Page>
      );
    }

    const isOwnListing =
      currentUser &&
      currentUser.id &&
      currentAuthor &&
      currentAuthor.id &&
      currentAuthor.id.uuid === currentUser.id.uuid;

    const hasListingAndAuthor = !!(currentListing.id && currentAuthor.id);

    const { currentTransaction } = bookingData || {};
    const { extendBookingData } =
      currentTransaction && currentTransaction.attributes
        ? currentTransaction.attributes.protectedData
        : {};

    const hasBookingData = !!(
      extendBookingData &&
      extendBookingData.startDate &&
      extendBookingData.endDate &&
      extendBookingData.extendPrice
    );

    const isFreeExtendBooking =
      extendBookingData &&
      extendBookingData.extendPrice &&
      extendBookingData.extendPrice.amount === 0;

    const hasRequiredData =
      hasListingAndAuthor && (!!transaction || (!transaction && hasBookingData));
    const canShowPage = hasRequiredData && !isOwnListing;
    const shouldRedirect = !isLoading && !canShowPage;

    // Redirect back to ListingPage if data is missing.
    // Redirection must happen before any data format error is thrown (e.g. wrong currency)
    if (shouldRedirect) {
      // eslint-disable-next-line no-console
      console.error('Missing or invalid data for checkout, redirecting back to listing page.', {
        transaction: speculatedTransaction,
        extendBookingData,
        listing,
      });
      return <NamedRedirect name="ListingPage" params={params} />;
    }

    // Show breakdown only when speculated transaction and booking are loaded
    // (i.e. have an id)
    const tx = existingTransaction.booking ? existingTransaction : speculatedTransaction;
    const txBooking = ensureBooking(tx.booking);
    const breakdown =
      tx.id && txBooking.id ? (
        <ExtendBookingBreakDown
          className={css.bookingBreakdown}
          userRole="customer"
          unitType={config.bookingUnitType}
          transaction={tx}
          booking={txBooking}
          dateType={DATE_TYPE_DATE}
        />
      ) : null;

    const isPaymentExpired = checkIsPaymentExpired(existingTransaction);

    const hasDefaultPaymentMethod = !!(
      stripeCustomerFetched &&
      ensureStripeCustomer(currentUser.stripeCustomer).attributes.stripeCustomerId &&
      ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).id
    );

    // Allow showing page when currentUser is still being downloaded,
    // but show payment form only when user info is loaded.
    const showPaymentForm = !!(
      currentUser &&
      hasRequiredData &&
      !listingNotFound &&
      !initiateOrderError &&
      !speculateTransactionError &&
      !retrievePaymentIntentError &&
      !isPaymentExpired &&
      !isFreeExtendBooking
    );

    const showDetailsOfFreeExtend = !showPaymentForm && isFreeExtendBooking;

    const firstImage =
      currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

    const listingLink = (
      <NamedLink
        name="ListingPage"
        params={{ id: currentListing.id.uuid, slug: createSlug(listingTitle) }}
      >
        <FormattedMessage id="CheckoutPage.errorlistingLinkText" />
      </NamedLink>
    );

    const isAmountTooLowError = isTransactionInitiateAmountTooLowError(initiateOrderError);
    const isChargeDisabledError = isTransactionChargeDisabledError(initiateOrderError);
    const isBookingTimeNotAvailableError = isTransactionInitiateBookingTimeNotAvailableError(
      initiateOrderError
    );
    const stripeErrors = transactionInitiateOrderStripeErrors(initiateOrderError);

    let initiateOrderErrorMessage = null;
    let listingNotFoundErrorMessage = null;

    if (listingNotFound) {
      listingNotFoundErrorMessage = (
        <p className={css.notFoundError}>
          <FormattedMessage id="CheckoutPage.listingNotFoundError" />
        </p>
      );
    } else if (isAmountTooLowError) {
      initiateOrderErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.initiateOrderAmountTooLow" />
        </p>
      );
    } else if (isBookingTimeNotAvailableError) {
      initiateOrderErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.bookingTimeNotAvailableMessage" />
        </p>
      );
    } else if (isChargeDisabledError) {
      initiateOrderErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.chargeDisabledMessage" />
        </p>
      );
    } else if (stripeErrors && stripeErrors.length > 0) {
      // NOTE: Error messages from Stripes are not part of translations.
      // By default they are in English.
      const stripeErrorsAsString = stripeErrors.join(', ');
      initiateOrderErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage
            id="CheckoutPage.initiateOrderStripeError"
            values={{ stripeErrors: stripeErrorsAsString }}
          />
        </p>
      );
    } else if (initiateOrderError) {
      // Generic initiate order error
      initiateOrderErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.initiateOrderError" values={{ listingLink }} />
        </p>
      );
    }

    const speculateTransactionErrorMessage = speculateTransactionError ? (
      <p className={css.speculateError}>
        <FormattedMessage id="CheckoutPage.speculateTransactionError" />
      </p>
    ) : null;
    let speculateErrorMessage = null;

    if (isTransactionInitiateMissingStripeAccountError(speculateTransactionError)) {
      speculateErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.providerStripeAccountMissingError" />
        </p>
      );
    } else if (isTransactionInitiateBookingTimeNotAvailableError(speculateTransactionError)) {
      speculateErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.bookingTimeNotAvailableMessage" />
        </p>
      );
    } else if (isTransactionZeroPaymentError(speculateTransactionError)) {
      speculateErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.initiateOrderAmountTooLow" />
        </p>
      );
    } else if (speculateTransactionError) {
      speculateErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.speculateFailedMessage" />
        </p>
      );
    }

    const declineErrorMessage = transitError ? (
      <p className={css.orderError}>
        <FormattedMessage id="ExtendCheckoutPage.transitError" />
      </p>
    ) : null;

    const { pricingType } = currentListing.attributes.publicData;
    const priceDuration = config.custom.priceDurationConfig.find(ele => ele.key === pricingType);
    const unitTranslationKey = priceDuration ? priceDuration.labelListingId : 'CheckoutPage.perDay';

    const price = currentListing.attributes.price;
    const formattedPrice = formatMoney(intl, price);
    const detailsSubTitle = `${formattedPrice} ${intl.formatMessage({ id: unitTranslationKey })}`;

    // Get first and last name of the current user and use it in the StripePaymentForm to autofill the name field
    const userName =
      currentUser && currentUser.attributes
        ? `${currentUser.attributes.profile.firstName} ${currentUser.attributes.profile.lastName}`
        : null;

    // If paymentIntent status is not waiting user action,
    // confirmCardPayment has been called previously.
    const hasPaymentIntentUserActionsDone =
      paymentIntent && STRIPE_PI_USER_ACTIONS_DONE_STATUSES.includes(paymentIntent.status);

    // If your marketplace works mostly in one country you can use initial values to select country automatically
    // e.g. {country: 'FI'}

    const phoneNumber =
      currentUser &&
      currentUser.attributes &&
      currentUser.attributes.profile &&
      currentUser.attributes.profile.protectedData &&
      currentUser.attributes.profile.protectedData.phoneNumber
        ? currentUser.attributes.profile.protectedData.phoneNumber
        : null;

    const initalValuesForStripePayment = { name: userName, phoneNumber, country: 'AU' };

    const shouldShowDecline =
      !transaction &&
      currentTransaction &&
      (txIsOfferExtendBooking(currentTransaction) || exTxIsOfferExtendBooking(currentTransaction));

    const declineButton = shouldShowDecline ? (
      <div className={css.buttonWrapper}>
        <SecondaryButton
          type={'button'}
          inProgress={this.state.submitting}
          disabled={this.state.submitting}
          onClick={() => this.handleDecline(currentTransaction)}
        >
          <FormattedMessage id="ExtendCheckoutPage.declineOffer" />
        </SecondaryButton>
      </div>
    ) : null;

    return (
      <Page {...pageProps}>
        {topbar}
        <div className={css.contentContainer}>
          <div className={css.aspectWrapper}>
            <ResponsiveImage
              rootClassName={css.rootForImage}
              alt={listingTitle}
              image={firstImage}
              variants={['landscape-crop', 'landscape-crop2x']}
            />
          </div>
          <div className={classNames(css.avatarWrapper, css.avatarMobile)}>
            <AvatarMedium user={currentAuthor} disableProfileLink />
          </div>
          <div className={css.bookListingContainer}>
            <div className={css.heading}>
              <h1 className={css.title}>{title}</h1>
              <div className={css.author}>
                <FormattedMessage
                  id="CheckoutPage.hostedBy"
                  values={{ name: currentAuthor.attributes.profile.displayName }}
                />
              </div>
            </div>

            <div className={css.priceBreakdownContainer}>
              {speculateTransactionErrorMessage}
              {breakdown}
            </div>

            <section className={css.paymentContainer}>
              {initiateOrderErrorMessage}
              {listingNotFoundErrorMessage}
              {speculateErrorMessage}
              {declineErrorMessage}
              {retrievePaymentIntentError ? (
                <p className={css.orderError}>
                  <FormattedMessage
                    id="CheckoutPage.retrievingStripePaymentIntentFailed"
                    values={{ listingLink }}
                  />
                </p>
              ) : null}
              {showDetailsOfFreeExtend ? (
                <>
                  <h3 className={css.paymentHeading}>
                    <FormattedMessage id="ExtendCheckoutPage.paymentHeading" />
                  </h3>
                  <PrimaryButton
                    className={css.submitButton}
                    type="button"
                    inProgress={this.state.submitting}
                    disabled={this.state.submitting}
                    onClick={this.handleSubmit}
                  >
                    <FormattedMessage id="StripePaymentForm.submitPaymentInfo" />
                  </PrimaryButton>
                </>
              ) : null}
              {showPaymentForm ? (
                <StripePaymentForm
                  className={css.paymentForm}
                  onSubmit={this.handleSubmit}
                  inProgress={this.state.submitting}
                  formId="CheckoutPagePaymentForm"
                  paymentInfo={intl.formatMessage({ id: 'CheckoutPage.paymentInfo' })}
                  authorDisplayName={currentAuthor.attributes.profile.displayName}
                  showInitialMessageInput={false}
                  initialValues={initalValuesForStripePayment}
                  initiateOrderError={initiateOrderError}
                  confirmCardPaymentError={confirmCardPaymentError}
                  confirmPaymentError={confirmPaymentError}
                  hasHandledCardPayment={hasPaymentIntentUserActionsDone}
                  loadingData={!stripeCustomerFetched}
                  defaultPaymentMethod={
                    hasDefaultPaymentMethod ? currentUser.stripeCustomer.defaultPaymentMethod : null
                  }
                  paymentIntent={paymentIntent}
                  onStripeInitialized={this.onStripeInitialized}
                />
              ) : null}
              {declineButton}
              {isPaymentExpired ? (
                <p className={css.orderError}>
                  <FormattedMessage
                    id="CheckoutPage.paymentExpiredMessage"
                    values={{ listingLink }}
                  />
                </p>
              ) : null}
            </section>
          </div>

          <div className={css.detailsContainerDesktop}>
            <div className={css.detailsAspectWrapper}>
              <ResponsiveImage
                rootClassName={css.rootForImage}
                alt={listingTitle}
                image={firstImage}
                variants={['landscape-crop', 'landscape-crop2x']}
              />
            </div>
            <div className={css.avatarWrapper}>
              <AvatarMedium user={currentAuthor} disableProfileLink />
            </div>
            <div className={css.detailsHeadings}>
              <h2 className={css.detailsTitle}>{listingTitle}</h2>
              <p className={css.detailsSubtitle}>{detailsSubTitle}</p>
            </div>
            {speculateTransactionErrorMessage}
            {breakdown}
          </div>
        </div>
      </Page>
    );
  }
}

ExtendCheckoutPageComponent.defaultProps = {
  initiateOrderError: null,
  confirmPaymentError: null,
  listing: null,
  bookingData: {},
  bookingDates: null,
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  currentUser: null,
  paymentIntent: null,
  transitError: null,
};

ExtendCheckoutPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  listing: propTypes.listing,
  bookingData: object,
  bookingDates: shape({
    bookingStart: instanceOf(Date).isRequired,
    bookingEnd: instanceOf(Date).isRequired,
  }),
  fetchStripeCustomer: func.isRequired,
  stripeCustomerFetched: bool.isRequired,
  fetchSpeculatedTransaction: func.isRequired,
  speculateTransactionInProgress: bool.isRequired,
  speculateTransactionError: propTypes.error,
  speculatedTransaction: propTypes.extendTransaction,
  transaction: propTypes.extendTransaction,
  currentUser: propTypes.currentUser,
  params: shape({
    id: string,
    slug: string,
  }).isRequired,
  onConfirmPayment: func.isRequired,
  onInitiateOrder: func.isRequired,
  onConfirmCardPayment: func.isRequired,
  onSavePaymentMethod: func.isRequired,
  onSendMessage: func.isRequired,
  initiateOrderError: propTypes.error,
  confirmPaymentError: propTypes.error,

  paymentIntent: object,

  // from connect
  dispatch: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  onCreateSetupIntent: func.isRequired,
  onHandleCardSetup: func.isRequired,
  onTransit: func.isRequired,
};

const mapStateToProps = state => {
  const {
    listing,
    bookingData,
    stripeCustomerFetched,
    speculateTransactionInProgress,
    speculateTransactionError,
    speculatedTransaction,
    transaction,
    initiateOrderError,
    confirmPaymentError,
    transitError,
  } = state.ExtendCheckoutPage;
  const { currentUser } = state.user;
  const { confirmCardPaymentError, paymentIntent, retrievePaymentIntentError } = state.stripe;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    stripeCustomerFetched,
    bookingData,
    speculateTransactionInProgress,
    speculateTransactionError,
    speculatedTransaction,
    transaction,
    listing,
    initiateOrderError,
    confirmPaymentError,
    confirmCardPaymentError,
    paymentIntent,
    retrievePaymentIntentError,
    transitError,
  };
};

const mapDispatchToProps = dispatch => ({
  dispatch,
  fetchSpeculatedTransaction: (params, transactionId) =>
    dispatch(speculateTransaction(params, transactionId)),
  fetchStripeCustomer: () => dispatch(stripeCustomer()),
  onInitiateOrder: (params, transactionId) => dispatch(initiateOrder(params, transactionId)),
  onConfirmCardPayment: params => dispatch(confirmCardPayment(params)),
  onConfirmPayment: params => dispatch(confirmPayment(params)),
  onSendMessage: params => dispatch(sendMessage(params)),
  onSavePaymentMethod: (stripeCustomer, stripePaymentMethodId) =>
    dispatch(savePaymentMethod(stripeCustomer, stripePaymentMethodId)),
  onCreateSetupIntent: params => dispatch(createStripeSetupIntent(params)),
  onHandleCardSetup: params => dispatch(handleCardSetup(params)),
  onSavePhoneNumber: params => dispatch(savePhoneNumber(params)),
  onRetrievePaymentIntent: params => dispatch(retrievePaymentIntent(params)),
  onTransit: (transactionId, transition, params) =>
    dispatch(transit(transactionId, transition, params)),
});

const ExtendCheckoutPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ExtendCheckoutPageComponent);

ExtendCheckoutPage.setInitialValues = initialValues => setInitialValues(initialValues);

ExtendCheckoutPage.displayName = 'CheckOutPage';

export default ExtendCheckoutPage;
